import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { useProjectBuilderStore } from '../store/project-builder.store'
import { ProjectListCard } from '../component/molecules/ProjectListCard'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useNavigate } from 'react-router-dom'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { Box, Typography } from '@mui/material'
import { ProjectOverviewCards } from '../component/molecules/ProjectOverviewCards'
import { DeleteProjectModal } from '../component/molecules/DeleteProjectModal'
import { ProjectListToolbar, ProjectStatusFilter, ProjectSortOption } from '../component/molecules/ProjectListToolbar'
import { ProjectDetailsCard } from '../component/molecules/ProjectDetailsCard'
import { ProjectInsightsCard } from '../component/molecules/ProjectInsightsCard'
import { Title1 } from 'src/feature/localization/component/atom/Title1'

export const ProjectBuilderOverviewPage = observer(() => {
  const { translation } = useLocalizationStore()
  const projectBuilderStore = useProjectBuilderStore()
  const navigate = useNavigate()
  const { isLoading } = projectBuilderStore.getGenericFieldsState('getProjects')
  const projectBuilderTranslation = translation.pages.projectBuilder

  // For delete project modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  // For selected project to view
  const [selectedProject, setSelectedProject] = useState({
    id: '',
    name: '',
  })

  // Filter/Search/Sort
  const [search, setSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<ProjectStatusFilter>('ALL')
  const [selectedSort, setSelectedSort] = useState<ProjectSortOption>('NONE')

  useEffect(() => {
    projectBuilderStore.getProjects()
  }, [projectBuilderStore])

  const projects = projectBuilderStore.projectsOverview

  // Filter and Sort projects
  const filteredProjects = useMemo(() => {
    const now = new Date()

    const filtered = projects.filter((project) => {
      const matchesSearch = project.name
        .toLowerCase()
        .includes(search.toLowerCase())

      const start = new Date(project.startDate)
      const end = new Date(project.endDate)

      const projectStatus = now < start
        ? 'PLANNED'
        : now > end
          ? 'FINISHED'
          : 'ACTIVE'

      const matchesFilter = selectedFilter === 'ALL'
        ? true
        : projectStatus === selectedFilter

      return matchesSearch && matchesFilter
    })
    filtered.sort((a, b) => {
      const aDate = new Date(a.startDate).getTime()
      const bDate = new Date(b.startDate).getTime()

      return selectedSort === 'NEWEST'
        ? bDate - aDate
        : aDate - bDate
    })

    return filtered
  }, [
    projects,
    search,
    selectedFilter,
    selectedSort,
  ])
  const hasProjects = projects.length > 0

  // Navigate to create project
  const handleCreateProject = () => {
    navigate(appRoutePath.projectBuilderCreate)
  }

  // View project by id
  const handleViewProject = (
    projectId: string,
    projectName: string,
  ) => {
    setSelectedProject({
      id: projectId,
      name: projectName,
    })
  }

  // Delete project
  const handleDeleteProject = (
    projectId: string,
    projectName: string,
  ) => {
    setSelectedProject({
      id: projectId,
      name: projectName,
    })

    setIsDeleteModalOpen(true)
  }

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
  }

  const handleConfirmDelete = async () => {
    await projectBuilderStore.deleteProject(selectedProject.id)

    setIsDeleteModalOpen(false)
  }

  return (
    <Box sx={styles.root}>
      <Title1 m={0} textValue={projectBuilderTranslation.overviewTitle} />
      <Typography variant='body2' sx={styles.subtitle}>
        {projectBuilderTranslation.overviewSubtitle()}
      </Typography>

      <AppBox sx={styles.contentLayout}>
        <AppBox sx={styles.leftColumn}>
          <ProjectOverviewCards
            projectCount={projects.length}
            onCreateProject={handleCreateProject}
          />
          <AppPaper sx={styles.projectsPaper}>
            <ProjectListToolbar
              selectedFilter={selectedFilter}
              selectedSort={selectedSort}
              onSearchChange={setSearch}
              onFilterChange={setSelectedFilter}
              onSortChange={setSelectedSort}
            />
            <AppBox sx={styles.cardContainer}>
              {!isLoading && filteredProjects.length > 0
                ? filteredProjects.map(project => (
                  <ProjectListCard
                    key={project.id}
                    projectId={project.id}
                    projectName={project.name}
                    users={project.users}
                    startDate={project.startDate}
                    endDate={project.endDate}
                    isSelected={selectedProject.id === project.id}
                    onShowProject={handleViewProject}
                    onDeleteProject={() => handleDeleteProject(
                      project.id,
                      project.name,
                    )}
                  />
                ))
                : !isLoading && (
                  <AppBox sx={styles.filteredEmptyState}>
                    <Typography sx={styles.emptyStateText}>
                      {hasProjects
                        ? projectBuilderTranslation.noProjectFound()
                        : projectBuilderTranslation.noProjects()}
                    </Typography>
                  </AppBox>
                )}
            </AppBox>
          </AppPaper>
        </AppBox>

        <AppBox sx={styles.rightColumn}>
          {selectedProject.id && (
            <ProjectInsightsCard
              project={projectBuilderStore.projectDetails}
            />
          )}

          <ProjectDetailsCard
            projectId={selectedProject.id}
          />
        </AppBox>
      </AppBox>
      <DeleteProjectModal
        open={isDeleteModalOpen}
        projectName={selectedProject.name}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    margin: { xs: '1rem', md: '1rem 5rem' },
    maxWidth: '100%',
  },
  contentLayout: {
    display: 'grid',
    gap: 20,
    gridTemplateColumns: {
      xs: '1fr',
      md: '1fr 1fr',
    },
    width: '100%',
  },
  leftColumn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  rightColumn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  projectsPaper: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    padding: '10px 15px',
    width: '100%',
    border: 0,
    gap: 20,
  },
  subtitle: {
    color: 'text.secondary',
    marginTop: '-15px',
  },
  cardContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  filteredEmptyState: {
    borderRadius: '10px',
    width: '100%',
    minHeight: '10em',
    padding: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 12,
    textAlign: 'center',
    color: 'text.secondary',
  },
} as const